import { Printer } from "lucide-react";
import React, { useState } from "react";

const BillingReportHTML = ({
  patientReport = {},
  opdForm = {},
  userDetails = {},
  btnTitle = "Generate Billing PDF",
}) => {
  const [showReport, setShowReport] = useState(false);

  // --- Data Getter Functions ---
  const getPatientName = () => opdForm?.userDetails?.name || userDetails?.userDetails?.name || "N/A";
  const getOpdNumber = () => patientReport?.opdNumber || "N/A";
  const getPhone = () => opdForm?.userDetails?.contact?.mobile || userDetails?.userDetails?.contact?.mobile || "N/A";
  
  // Added Age and Gender getters
  const getAge = () => (opdForm?.age ? `${opdForm.age} years` : "N/A");
  const getGender = () => opdForm?.userDetails?.gender || userDetails?.userDetails?.gender || "N/A";

  // Financial data getters
  const getBillingItems = () => patientReport?.financials?.billingItems || [];
  const getTotalCost = () => patientReport?.financials?.totalCost || 0;
  
  const getVisits = () => {
    const visits = patientReport?.financials?.visits;
    return Array.isArray(visits) ? visits.filter(v => v && v.date) : [];
  };

  const getTotalPaid = () => {
    return getVisits().reduce((acc, visit) => acc + (parseFloat(visit.amount) || 0), 0);
  };

  const getBalanceDue = () => {
    return getTotalCost() - getTotalPaid();
  };

  // --- Handlers ---
  const generateReport = () => setShowReport(true);
  const closeReport = () => setShowReport(false);
  const printReport = () => window.print();

  // --- Report Content Sub-component ---
  const ReportContent = () => (
    <div
      id="billing-report-content"
      style={{
        fontFamily: "Arial, sans-serif",
        maxWidth: "800px",
        minWidth: "670px",
        margin: "0 auto",
        padding: "20px",
        backgroundColor: "white",
        color: "#333",
        fontSize: "11px",
        lineHeight: "1.4",
      }}
    >
      {/* Header Section */}
      <div style={{ textAlign: "center", marginBottom: "20px", borderBottom: "3px solid #2563eb", paddingBottom: "15px" }}>
        <img
          src="/Mahalaxmi-header.jpg"
          alt="Clinic Header"
          style={{ width: "100%", height: "auto", objectFit: "contain" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      </div>

      {/* MODIFIED: Patient & Invoice Info Section */}
      <div style={{ background: "#f8fafc", border: "2px solid #e2e8f0", padding: "15px", marginBottom: "20px", borderRadius: "8px" }}>
        <h2 style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold", marginBottom: "12px", color: "#1f2937" }}>
          Patient Invoice
        </h2>
        {/* Changed to 3 columns for better layout */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "10px" }}>
          {/* Column 1 */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px dotted #d1d5db" }}>
              <span style={{ fontWeight: "bold", color: "#374151" }}>Patient Name:</span>
              <span style={{ color: "#6b7280" }}>{getPatientName()}</span>
            </div>
             <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px dotted #d1d5db" }}>
              <span style={{ fontWeight: "bold", color: "#374151" }}>Age:</span>
              <span style={{ color: "#6b7280" }}>{getAge()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px dotted #d1d5db" }}>
              <span style={{ fontWeight: "bold", color: "#374151" }}>Phone:</span>
              <span style={{ color: "#6b7280" }}>{getPhone()}</span>
            </div>
          </div>
          {/* Column 2 */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px dotted #d1d5db" }}>
              <span style={{ fontWeight: "bold", color: "#374151" }}>OPD Number:</span>
              <span style={{ color: "green", fontWeight: "bold" }}>{getOpdNumber()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px dotted #d1d5db" }}>
              <span style={{ fontWeight: "bold", color: "#374151" }}>OPD Date:</span>
              <span style={{ color: "#6b7280" }}>{new Date().toLocaleDateString("en-GB")}</span>
            </div>
             <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px dotted #d1d5db" }}>
              <span style={{ fontWeight: "bold", color: "#374151" }}>Gender:</span>
              <span style={{ color: "#6b7280" }}>{getGender()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Items Table */}
      <div style={{ border: "2px solid #e2e8f0", padding: "15px", borderRadius: "8px", marginBottom: "20px" }}>
        <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "12px", color: "#1f2937", textTransform: "uppercase" }}>
          Billing Details
        </h3>
        {getBillingItems().length > 0 ? (
          <div>
            {/* Table Header */}
            <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1.5fr 1.5fr", gap: "1px", backgroundColor: "#e2e8f0", border: "1px solid #d1d5db", borderRadius: "4px 4px 0 0", fontSize: "10px", textAlign: "center" }}>
              <div style={{ padding: "8px", fontWeight: "bold", backgroundColor: "#f8fafc", borderRight: "1px solid #d1d5db", textAlign: "left" }}>Procedure / Item</div>
              <div style={{ padding: "8px", fontWeight: "bold", backgroundColor: "#f8fafc", borderRight: "1px solid #d1d5db" }}>Qty</div>
              <div style={{ padding: "8px", fontWeight: "bold", backgroundColor: "#f8fafc", borderRight: "1px solid #d1d5db" }}>Unit Price (₹)</div>
              <div style={{ padding: "8px", fontWeight: "bold", backgroundColor: "#f8fafc" }}>Total (₹)</div>
            </div>
            {/* Table Rows */}
            {getBillingItems().map((item, index) => (
              <div key={index} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1.5fr 1.5fr", gap: "1px", backgroundColor: "#e2e8f0", border: "1px solid #d1d5db", borderTop: "none" }}>
                <div style={{ padding: "8px", backgroundColor: "white", borderRight: "1px solid #d1d5db" }}>{item.name || "N/A"}</div>
                <div style={{ padding: "8px", backgroundColor: "white", borderRight: "1px solid #d1d5db", textAlign: "center" }}>{item.quantity || 1}</div>
                <div style={{ padding: "8px", backgroundColor: "white", borderRight: "1px solid #d1d5db", textAlign: "right" }}>{item.pricePerItem?.toFixed(2) || "0.00"}</div>
                <div style={{ padding: "8px", backgroundColor: "white", textAlign: "right", fontWeight: "500" }}>{item.totalCostPerItem?.toFixed(2) || "0.00"}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: "#6b7280", padding: "10px", background: "#f9fafb", borderRadius: "4px" }}>No billing items found.</div>
        )}
      </div>

      {/* Financial Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Payment History */}
        <div style={{ border: "2px solid #e2e8f0", padding: "15px", borderRadius: "8px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "12px", color: "#1f2937", textTransform: "uppercase" }}>
                Payment History
            </h3>
            {getVisits().length > 0 ? (
                getVisits().map((visit, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dotted #d1d5db', fontSize: '10px' }}>
                        <span>{new Date(visit.date).toLocaleDateString("en-GB")} - {visit.reason || 'Payment'}</span>
                        <span style={{ fontWeight: 'bold' }}>₹{parseFloat(visit.amount || 0).toFixed(2)}</span>
                    </div>
                ))
            ) : (
                <div style={{ color: "#6b7280" }}>No payment history available.</div>
            )}
        </div>

        {/* Totals */}
        <div style={{ background: "#f8fafc", border: "2px solid #e2e8f0", padding: "15px", borderRadius: "8px", display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #d1d5db" }}>
            <span style={{ fontWeight: "bold", color: "#374151" }}>Total Cost:</span>
            <span style={{ fontWeight: "bold", color: "#1f2937" }}>₹{getTotalCost().toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #d1d5db" }}>
            <span style={{ fontWeight: "bold", color: "#374151" }}>Total Paid:</span>
            <span style={{ fontWeight: "bold", color: "green" }}>₹{getTotalPaid().toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", background: '#e2e8f0', borderRadius: '4px', marginTop: '5px' }}>
            <span style={{ fontWeight: "bold", color: "#374151" }}>Balance Due:</span>
            <span style={{ fontWeight: "bold", color: "red" }}>₹{getBalanceDue().toFixed(2)}</span>
          </div>
        </div>
      </div>
      
      {/* Footer & Signature */}
      <div style={{ marginTop: '50px', textAlign: 'right' }}>
        <div style={{ borderBottom: "1px solid #374151", margin: "20px 0 8px auto", height: "30px", width: "150px" }}></div>
        <div style={{ fontSize: "11px", fontWeight: "bold" }}>Doctor Signature</div>
        <div style={{ fontSize: "12px", fontWeight: "bold", color: "#6b7280", marginTop: "3px" }}>Dr. Tushar S. Maturkar</div>
        <div style={{ fontSize: "10px", color: "#6b7280" }}>BDS (Dental Surgeon)</div>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={generateReport}
        className="w-full sm:w-auto px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-200 transition-all duration-200 flex items-center justify-center gap-2"
      >
        <Printer size={18} />
        {btnTitle}
      </button>

      {showReport && (
        <>
          <style>
            {`
              @media print {
                body * { visibility: hidden; }
                #billing-report-content, #billing-report-content * { visibility: visible; }
                #billing-report-content {
                  position: absolute; left: 0; top: 0; width: 100%;
                  margin: 0; padding: 15mm; font-size: 11px !important;
                }
                @page { size: A4; margin: 10mm; }
                html { -webkit-print-color-adjust: exact; }
              }
            `}
          </style>

          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">Patient Billing Invoice</h2>
                <div className="flex items-center gap-3">
                  <button onClick={printReport} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                    <Printer size={16} /> Print
                  </button>
                  <button onClick={closeReport} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[calc(95vh-70px)] bg-white">
                <ReportContent />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BillingReportHTML;